import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Car, 
  Plus, 
  Search, 
  Package,
  Trash2,
  Edit,
  X,
  ArrowRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { productsAPI, carsAPI, carTransfersAPI, carSalesAPI } from '../utils/api';
import PageToolbar from '../components/PageToolbar';

const Cars = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  
  // State for cars
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [carPlate, setCarPlate] = useState('');

  // State for transfers
  const [products, setProducts] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferProductId, setTransferProductId] = useState('');
  const [transferQuantity, setTransferQuantity] = useState('');
  const [transfers, setTransfers] = useState([]);
  const [showTransfers, setShowTransfers] = useState(false);
  const [carStocks, setCarStocks] = useState({});
  const [showEditStockModal, setShowEditStockModal] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  const [newStockQuantity, setNewStockQuantity] = useState('');

  // State for sales
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [saleProductId, setSaleProductId] = useState('');
  const [saleQuantity, setSaleQuantity] = useState('');
  
  // Loading states for buttons
  const [isAddingCar, setIsAddingCar] = useState(false);
  const [isUpdatingCar, setIsUpdatingCar] = useState(false);
  const [isDeletingCar, setIsDeletingCar] = useState(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const [isDeletingTransfer, setIsDeletingTransfer] = useState(null);
  const [isUpdatingStock, setIsUpdatingStock] = useState(false);
  const [isSavingSale, setIsSavingSale] = useState(false);

  // Fetch cars
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch cars
      const carsResponse = await carsAPI.getAll({ search: searchTerm });
      setCars(carsResponse.data.cars || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      showError('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, showError]);

  // Fetch products for transfer
  const fetchProducts = useCallback(async () => {
    try {
      const response = await productsAPI.getProducts({ limit: 1000 });
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      showError('Ürünler yüklenirken hata oluştu');
    }
  }, [showError]);

  // Fetch transfers for a car
  const fetchTransfers = useCallback(async (carId) => {
    try {
      const response = await carTransfersAPI.getByCarId(carId);
      setTransfers(response.data.transfers || []);
    } catch (error) {
      console.error('Error fetching transfers:', error);
      showError('Transferler yüklenirken hata oluştu');
    }
  }, [showError]);

  // Fetch stock for all cars
  const fetchCarStocks = useCallback(async () => {
    if (!cars.length) {
      setCarStocks({});
      return;
    }

    try {
      const stockEntries = await Promise.all(
        cars.map(async (car) => {
          const [transfersResponse, salesResponse] = await Promise.all([
            carTransfersAPI.getByCarId(car.id),
            carSalesAPI.getByCarId(car.id)
          ]);

          const transfers = transfersResponse.data.transfers || [];
          const sales = salesResponse.data.sales || [];

          const productStocks = {};

          transfers.forEach((transfer) => {
            const key = `${transfer.productId}`;
            if (!productStocks[key]) {
              productStocks[key] = {
                productId: transfer.productId,
                productName: transfer.productName,
                productBrand: transfer.productBrand,
                quantity: 0
              };
            }
            productStocks[key].quantity += transfer.quantity;
          });

          sales.forEach((sale) => {
            const key = `${sale.productId}`;
            if (!productStocks[key]) {
              productStocks[key] = {
                productId: sale.productId,
                productName: sale.productName,
                productBrand: sale.productBrand,
                quantity: 0
              };
            }
            productStocks[key].quantity -= sale.quantity;
          });

          const stockList = Object.values(productStocks).filter((p) => p.quantity > 0);
          return [car.id, stockList];
        })
      );

      const nextStocks = stockEntries.reduce((acc, [carId, stockList]) => {
        acc[carId] = stockList;
        return acc;
      }, {});

      setCarStocks(nextStocks);
    } catch (error) {
      console.error('Error fetching car stocks:', error);
      showError('Araç stokları alınırken hata oluştu');
    }
  }, [cars, showError]);

  // Cars are already filtered by API, so use them directly
  const filteredCars = cars;

  const fleetSummary = useMemo(() => {
    const productSet = new Set();
    let totalQuantity = 0;

    Object.values(carStocks).forEach((stockList) => {
      stockList.forEach((stock) => {
        productSet.add(stock.productId);
        totalQuantity += stock.quantity || 0;
      });
    });

    return {
      totalCars: cars.length,
      distinctProducts: productSet.size,
      totalQuantity
    };
  }, [cars, carStocks]);

  // Handle adding car
  const handleAddCar = async () => {
    if (!carPlate.trim()) {
      showError('Lütfen plaka girin');
      return;
    }
    if (isAddingCar) return;

    try {
      setIsAddingCar(true);
      const carData = {
        plate: carPlate.trim().toUpperCase(),
        productId: null, // Araç ekleme sadece plaka ile
        createdBy: user?.id
      };

      const response = await carsAPI.create(carData);
      const newCar = response.data.car;
      
      setCars(prev => [newCar, ...prev]);
      setShowAddModal(false);
      setCarPlate('');
      showSuccess('Araba başarıyla eklendi');
    } catch (error) {
      console.error('Error adding car:', error);
      showError('Araba eklenirken hata oluştu');
    } finally {
      setIsAddingCar(false);
    }
  };

  // Handle editing car
  const handleEditCar = (car) => {
    setEditingCar(car);
    setCarPlate(car.plate);
  };

  // Handle updating car
  const handleUpdateCar = async () => {
    if (!carPlate.trim()) {
      showError('Plaka boş olamaz');
      return;
    }
    if (isUpdatingCar) return;

    try {
      setIsUpdatingCar(true);
      const carData = {
        plate: carPlate.trim().toUpperCase(),
        updatedBy: user?.id
      };

      const response = await carsAPI.update(editingCar.id, carData);
      const updatedCar = response.data.car;
      
      setCars(prev => prev.map(car => 
        car.id === editingCar.id ? updatedCar : car
      ));
      
      setEditingCar(null);
      setCarPlate('');
      showSuccess('Araba başarıyla güncellendi');
    } catch (error) {
      console.error('Error updating car:', error);
      showError('Araba güncellenirken hata oluştu');
    } finally {
      setIsUpdatingCar(false);
    }
  };

  // Handle deleting car
  const handleDeleteCar = async (carId) => {
    if (!window.confirm('Bu arabayı silmek istediğinizden emin misiniz?')) {
      return;
    }
    if (isDeletingCar === carId) return;

    try {
      setIsDeletingCar(carId);
      await carsAPI.delete(carId);
      
      setCars(prev => prev.filter(car => car.id !== carId));
      showSuccess('Araba başarıyla silindi');
    } catch (error) {
      console.error('Error deleting car:', error);
      showError('Araba silinirken hata oluştu');
    } finally {
      setIsDeletingCar(null);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingCar(null);
    setCarPlate('');
  };

  // Handle transfer modal
  const handleTransferModal = (car) => {
    setSelectedCar(car);
    setShowTransferModal(true);
    setTransferProductId('');
    setTransferQuantity('');
  };

  // Handle transfer
  const handleTransfer = async () => {
    const quantity = parseInt(transferQuantity);
    
    if (!transferProductId || !transferQuantity || isNaN(quantity) || quantity <= 0) {
      showError('Lütfen ürün seçin ve geçerli bir miktar girin');
      return;
    }

    const product = products.find(p => p.id === transferProductId);
    if (product && quantity > product.stock) {
      showError(`Miktar stoktan fazla olamaz. Mevcut stok: ${product.stock}`);
      return;
    }
    if (isTransferring) return;

    try {
      setIsTransferring(true);
      const transferData = {
        carId: selectedCar.id,
        productId: transferProductId,
        quantity: quantity,
        notes: '',
        createdBy: user?.id
      };

      const transferResult = await carTransfersAPI.create(transferData);
      
      // Update product stock with movement tracking
      if (product) {
        await productsAPI.updateStock(transferProductId, -quantity, {
          movementType: 'transfer_out',
          referenceType: 'car_transfer',
          referenceId: transferResult.data.id,
          notes: `Araç transferi - ${selectedCar.plate}`,
          createdBy: user?.id
        });
      }

      setShowTransferModal(false);
      setSelectedCar(null);
      setTransferProductId('');
      setTransferQuantity('');
      
      // Refresh data
      fetchData();
      fetchProducts();
      fetchCarStocks();
      
      showSuccess('Transfer başarıyla yapıldı');
    } catch (error) {
      console.error('Error creating transfer:', error);
      showError('Transfer yapılırken hata oluştu');
    } finally {
      setIsTransferring(false);
    }
  };

  // Handle show transfers
  const handleShowTransfers = async (car) => {
    setSelectedCar(car);
    setShowTransfers(true);
    await fetchTransfers(car.id);
  };

  // Handle delete transfer
  const handleDeleteTransfer = async (transferId) => {
    if (!window.confirm('Bu transferi silmek istediğinizden emin misiniz? Transfer edilen stok ana stoğa geri eklenecek.')) {
      return;
    }
    if (isDeletingTransfer === transferId) return;

    try {
      setIsDeletingTransfer(transferId);
      // First, get transfer details to know what to add back to main stock
      const transfer = transfers.find(t => t.id === transferId);
      if (!transfer) {
        showError('Transfer bulunamadı');
        return;
      }

      // Delete the transfer
      await carTransfersAPI.delete(transferId);

      // Add the quantity back to main stock
      if (transfer.quantity > 0) {
        await productsAPI.updateStock(transfer.productId, transfer.quantity, {
          movementType: 'transfer_in',
          referenceType: 'car_transfer',
          referenceId: transferId,
          notes: `Transfer silme - ${selectedCar.plate}`,
          createdBy: user?.id
        });
      }

      // Refresh data
      await fetchTransfers(selectedCar.id);
      await fetchCarStocks();
      await fetchProducts(); // Refresh products to show updated stock

      showSuccess('Transfer silindi ve stok ana stoğa geri eklendi');
    } catch (error) {
      console.error('Error deleting transfer:', error);
      showError('Transfer silinirken hata oluştu');
    } finally {
      setIsDeletingTransfer(null);
    }
  };

  // Handle edit stock modal
  const handleEditStockModal = (car, stock) => {
    setSelectedCar(car);
    setEditingStock(stock);
    setNewStockQuantity(stock.quantity.toString());
    setShowEditStockModal(true);
  };

  // Handle update stock
  const handleUpdateStock = async () => {
    const newQuantity = parseInt(newStockQuantity);
    
    if (!newQuantity || isNaN(newQuantity) || newQuantity < 0) {
      showError('Lütfen geçerli bir miktar girin');
      return;
    }

    if (newQuantity === editingStock.quantity) {
      showError('Miktar değişmemiş');
      return;
    }
    if (isUpdatingStock) return;

    try {
      setIsUpdatingStock(true);
      const quantityDifference = newQuantity - editingStock.quantity;
      
      if (quantityDifference > 0) {
        // Stok artırılıyor - yeni transfer ekle
        const transferData = {
          carId: selectedCar.id,
          productId: editingStock.productId,
          quantity: quantityDifference,
          notes: 'Stok düzenleme - artırma',
          createdBy: user?.id
        };
        const transferResult = await carTransfersAPI.create(transferData);
        
        // Ana stoktan düş
        const product = products.find(p => p.id === editingStock.productId);
        if (product) {
          await productsAPI.updateStock(editingStock.productId, -quantityDifference, {
            movementType: 'transfer_out',
            referenceType: 'car_transfer',
            referenceId: transferResult.data.id,
            notes: `Araç stok artırma - ${selectedCar.plate}`,
            createdBy: user?.id
          });
        }
      } else if (quantityDifference < 0) {
        // Stok azaltılıyor - transfer sil veya azalt
        const absoluteDifference = Math.abs(quantityDifference);
        
        // Bu ürün için mevcut transferleri getir
        const response = await carTransfersAPI.getByCarId(selectedCar.id);
        const productTransfers = response.data.transfers.filter(t => t.productId === editingStock.productId);
        
        // Transferleri azalt veya sil
        let remainingToReduce = absoluteDifference;
        for (const transfer of productTransfers) {
          if (remainingToReduce <= 0) break;
          
          if (transfer.quantity <= remainingToReduce) {
            // Transferi tamamen sil
            await carTransfersAPI.delete(transfer.id);
            remainingToReduce -= transfer.quantity;
          } else {
            // Transferi azalt
            const newTransferQuantity = transfer.quantity - remainingToReduce;
            // Transfer güncelleme API'si yoksa sil ve yeni oluştur
            await carTransfersAPI.delete(transfer.id);
            if (newTransferQuantity > 0) {
              await carTransfersAPI.create({
                carId: selectedCar.id,
                productId: editingStock.productId,
                quantity: newTransferQuantity,
                notes: 'Stok düzenleme - azaltma',
                createdBy: user?.id
              });
            }
            remainingToReduce = 0;
          }
        }
        
        // Ana stoka geri ekle
        const product = products.find(p => p.id === editingStock.productId);
        if (product) {
          await productsAPI.updateStock(editingStock.productId, absoluteDifference, {
            movementType: 'transfer_in',
            referenceType: 'car_transfer',
            referenceId: null,
            notes: `Araç stok azaltma - ${selectedCar.plate}`,
            createdBy: user?.id
          });
        }
      }

      setShowEditStockModal(false);
      setSelectedCar(null);
      setEditingStock(null);
      setNewStockQuantity('');
      
      // Refresh data
      fetchData();
      fetchProducts();
      fetchCarStocks();
      
      showSuccess('Stok başarıyla güncellendi');
    } catch (error) {
      console.error('Error updating stock:', error);
      showError('Stok güncellenirken hata oluştu');
    } finally {
      setIsUpdatingStock(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchProducts();
  }, [fetchData, fetchProducts]);

  // Fetch car stocks when cars change
  useEffect(() => {
    fetchCarStocks();
  }, [cars, fetchCarStocks]);

  // Search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchData();
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, fetchData]);

  return (
    <div className="flex h-full flex-col bg-gray-50">
      <PageToolbar
        right={
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
            <div className="relative w-full md:w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Plaka veya araç bilgisi ara"
                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-700 placeholder-gray-400 focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
            <div className="text-sm text-gray-500 md:text-right">
              {filteredCars.length} araç listeleniyor
            </div>
          </div>
        }
      >
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-indigo-700"
        >
          <Plus className="h-4 w-4" />
          Araç Ekle
        </button>
      </PageToolbar>

      <div className="border-b bg-gray-50 px-6 py-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Toplam Araç</p>
                <p className="mt-2 text-2xl font-semibold text-gray-900">{fleetSummary.totalCars}</p>
              </div>
              <div className="rounded-full bg-indigo-100 p-2 text-indigo-600">
                <Car className="h-5 w-5" />
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Ürün Çeşidi</p>
                <p className="mt-2 text-2xl font-semibold text-gray-900">{fleetSummary.distinctProducts}</p>
              </div>
              <div className="rounded-full bg-emerald-100 p-2 text-emerald-600">
                <Package className="h-5 w-5" />
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Toplam Araç Stoğu</p>
                <p className="mt-2 text-2xl font-semibold text-gray-900">{fleetSummary.totalQuantity}</p>
              </div>
              <div className="rounded-full bg-amber-100 p-2 text-amber-600">
                <ArrowRight className="h-5 w-5" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden px-6 py-4">
        {loading ? (
          <div className="flex h-full flex-col items-center justify-center text-sm text-gray-500">
            <div className="mb-3 h-10 w-10 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600"></div>
            Araç bilgileri yükleniyor...
          </div>
        ) : filteredCars.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center text-sm text-gray-500">
            <Car className="mb-3 h-10 w-10 text-gray-300" />
            Henüz araç eklenmemiş. Yeni araç eklemek için üstteki butonu kullanın.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {filteredCars.map((car) => {
              const carStockList = carStocks[car.id] || [];
              const totalQuantity = carStockList.reduce((sum, item) => sum + (item.quantity || 0), 0);

              return (
                <div
                  key={car.id}
                  className="flex h-full flex-col rounded-xl border border-gray-200 bg-white shadow-sm transition hover:border-indigo-200 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3">
                    <div>
                      <div className="flex items-center gap-2 text-gray-900">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                          <Car className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold">{car.plate}</h3>
                          <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-600">
                            <Package className="h-3 w-3" /> {totalQuantity} adet stok
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEditCar(car)}
                        disabled={isDeletingCar === car.id}
                        className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-indigo-50 hover:text-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Araç bilgilerini düzenle"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCar(car.id)}
                        disabled={isDeletingCar === car.id}
                        className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Aracı sil"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-hidden px-4 py-3">
                    <h4 className="mb-2 text-sm font-medium text-gray-700">Araçtaki Ürünler</h4>
                    {carStockList.length === 0 ? (
                      <div className="flex h-full flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 py-6 text-xs text-gray-500">
                        Bu araç için stok transferi yapılmamış.
                      </div>
                    ) : (
                      <ul className="space-y-2 overflow-y-auto pr-1 text-sm max-h-40">
                        {carStockList.map((stock) => (
                          <li
                            key={`${car.id}-${stock.productId}`}
                            className="flex items-center justify-between rounded-lg border border-gray-100 bg-white px-3 py-2 text-gray-700 shadow-sm"
                          >
                            <div className="mr-2 min-w-0 flex-1">
                              <p className="truncate font-medium text-gray-900">
                                {stock.productBrand} {stock.productName}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="inline-flex min-w-[36px] justify-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-600">
                                {stock.quantity}
                              </span>
                              <button
                                onClick={() => handleEditStockModal(car, stock)}
                                className="rounded-md p-1 text-gray-400 transition-colors hover:bg-indigo-50 hover:text-indigo-600"
                                title="Stok miktarını düzenle"
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="border-t border-gray-100 px-4 py-3">
                    <div className="grid gap-2 sm:grid-cols-2">
                      <button
                        onClick={() => handleTransferModal(car)}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
                      >
                        <ArrowRight className="h-4 w-4" />
                        Stok Transferi
                      </button>
                      <button
                        onClick={() => {
                          setSelectedCar(car);
                          setShowSalesModal(true);
                          setSaleProductId('');
                          setSaleQuantity('');
                        }}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                      >
                        <Package className="h-4 w-4" />
                        Araçtan Satış
                      </button>
                      <button
                        onClick={() => handleShowTransfers(car)}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-sm font-medium text-purple-700 transition-colors hover:border-purple-300 hover:bg-purple-100 sm:col-span-2"
                      >
                        Transfer Geçmişi
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Car Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Araç Ekle</h2>
            
            {/* Car Plate */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plaka *
              </label>
              <input
                type="text"
                value={carPlate}
                onChange={(e) => setCarPlate(e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Plaka girin (örn: 34 ABC 123)"
                maxLength={10}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setCarPlate('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleAddCar}
                disabled={isAddingCar}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAddingCar ? 'İşleniyor...' : 'Ekle'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Car Modal */}
      {editingCar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Araç Düzenle</h2>
            
            {/* Car Plate */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plaka *
              </label>
              <input
                type="text"
                value={carPlate}
                onChange={(e) => setCarPlate(e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Plaka girin (örn: 34 ABC 123)"
                maxLength={10}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleUpdateCar}
                disabled={isUpdatingCar}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdatingCar ? 'İşleniyor...' : 'Güncelle'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && selectedCar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Stok Transfer Et - {selectedCar.plate}
            </h2>
            
            {/* Product Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ürün Seçin *
              </label>
              <select
                value={transferProductId}
                onChange={(e) => setTransferProductId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Ürün seçin...</option>
                {products.filter(p => p.stock > 0).map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.brand} - {product.name} (Stok: {product.stock})
                  </option>
                ))}
              </select>
            </div>

            {/* Quantity */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Miktar *
              </label>
              <input
                type="number"
                min="1"
                max={products.find(p => p.id === transferProductId)?.stock || 0}
                value={transferQuantity}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || (!isNaN(value) && parseInt(value) >= 0)) {
                    setTransferQuantity(value);
                  }
                }}
                onKeyDown={(e) => {
                  // Allow: backspace, delete, tab, escape, enter
                  if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
                      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                      (e.keyCode === 65 && e.ctrlKey === true) ||
                      (e.keyCode === 67 && e.ctrlKey === true) ||
                      (e.keyCode === 86 && e.ctrlKey === true) ||
                      (e.keyCode === 88 && e.ctrlKey === true) ||
                      // Allow: home, end, left, right
                      (e.keyCode >= 35 && e.keyCode <= 39)) {
                    return;
                  }
                  // Ensure that it is a number and stop the keypress
                  if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
                    e.preventDefault();
                  }
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Miktar girin..."
              />
              {transferProductId && (
                <p className="text-sm text-gray-500 mt-1">
                  Mevcut stok: {products.find(p => p.id === transferProductId)?.stock || 0}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowTransferModal(false);
                  setSelectedCar(null);
                  setTransferProductId('');
                  setTransferQuantity('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleTransfer}
                disabled={isTransferring}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isTransferring ? 'İşleniyor...' : 'Transfer Et'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfers List Modal */}
      {showTransfers && selectedCar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Transferler - {selectedCar.plate}
              </h2>
              <button
                onClick={() => {
                  setShowTransfers(false);
                  setSelectedCar(null);
                  setTransfers([]);
                }}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {transfers.length > 0 ? (
                <div className="space-y-3">
                  {transfers.map((transfer) => (
                    <div
                      key={transfer.id}
                      className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <ArrowRight className="w-5 h-5 text-green-600" />
                            <div>
                              <h3 className="font-semibold text-gray-900">
                                {transfer.productBrand} - {transfer.productName}
                              </h3>
                              <p className="text-sm text-gray-600">
                                Miktar: {transfer.quantity} | 
                                Tarih: {new Date(transfer.transferDate).toLocaleDateString('tr-TR')}
                              </p>
                              {transfer.notes && (
                                <p className="text-sm text-gray-500 mt-1">
                                  Not: {transfer.notes}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteTransfer(transfer.id)}
                          disabled={isDeletingTransfer === transfer.id}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Henüz transfer yapılmamış</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Stock Modal */}
      {showEditStockModal && selectedCar && editingStock && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Stok Düzenle - {selectedCar.plate}
            </h2>
            
            {/* Product Info */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-900">{editingStock.productBrand} - {editingStock.productName}</p>
              <p className="text-sm text-gray-600">Mevcut miktar: {editingStock.quantity} adet</p>
            </div>

            {/* New Quantity */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Yeni Miktar *
              </label>
              <input
                type="number"
                min="0"
                value={newStockQuantity}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || (!isNaN(value) && parseInt(value) >= 0)) {
                    setNewStockQuantity(value);
                  }
                }}
                onKeyDown={(e) => {
                  // Allow: backspace, delete, tab, escape, enter
                  if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
                      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                      (e.keyCode === 65 && e.ctrlKey === true) ||
                      (e.keyCode === 67 && e.ctrlKey === true) ||
                      (e.keyCode === 86 && e.ctrlKey === true) ||
                      (e.keyCode === 88 && e.ctrlKey === true) ||
                      // Allow: home, end, left, right
                      (e.keyCode >= 35 && e.keyCode <= 39)) {
                    return;
                  }
                  // Ensure that it is a number and stop the keypress
                  if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
                    e.preventDefault();
                  }
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Yeni miktar girin..."
              />
              <p className="text-sm text-gray-500 mt-1">
                {parseInt(newStockQuantity) > editingStock.quantity 
                  ? `+${parseInt(newStockQuantity) - editingStock.quantity} adet eklenecek`
                  : parseInt(newStockQuantity) < editingStock.quantity
                  ? `${editingStock.quantity - parseInt(newStockQuantity)} adet ana stoka geri eklenecek`
                  : 'Miktar değişmeyecek'
                }
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowEditStockModal(false);
                  setSelectedCar(null);
                  setEditingStock(null);
                  setNewStockQuantity('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleUpdateStock}
                disabled={isUpdatingStock}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdatingStock ? 'İşleniyor...' : 'Güncelle'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Car Sales Modal */}
      {showSalesModal && selectedCar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Araçtan Satış Yap - {selectedCar.plate}
            </h2>

            {/* Product Selection from car stock */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Ürün Seçin *</label>
              <select
                value={saleProductId}
                onChange={(e) => setSaleProductId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Ürün seçin...</option>
                {(carStocks[selectedCar.id] || []).map((stock) => (
                  <option key={stock.productId} value={stock.productId}>
                    {stock.productBrand} - {stock.productName} (Araç stok: {stock.quantity})
                  </option>
                ))}
              </select>
            </div>

            {/* Quantity */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Miktar *</label>
              <input
                type="number"
                min="1"
                value={saleQuantity}
                onChange={(e) => setSaleQuantity(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Miktar girin"
              />
            </div>

            {/* Auto price info */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Birim Fiyat (₺)</label>
              <p className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                {(() => {
                  const p = products.find(pr => pr.id === saleProductId);
                  return p ? Number(p.price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';
                })()}
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setShowSalesModal(false); setSelectedCar(null); setSaleProductId(''); setSaleQuantity(''); }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={async () => {
                  if (isSavingSale) return;
                  try {
                    setIsSavingSale(true);
                    const quantity = parseInt(saleQuantity);
                    if (!saleProductId || !quantity || quantity <= 0 || isNaN(quantity)) {
                      showError('Lütfen ürün ve geçerli miktar girin');
                      return;
                    }
                    const product = products.find(pr => pr.id === saleProductId);
                    const unitPrice = product ? parseFloat(product.price || 0) : 0;
                    if (!unitPrice || isNaN(unitPrice) || unitPrice <= 0) {
                      showError('Ürün fiyatı bulunamadı veya geçersiz');
                      return;
                    }
                    const carStockForProduct = (carStocks[selectedCar.id] || []).find(s => s.productId === saleProductId);
                    if (!carStockForProduct || quantity > carStockForProduct.quantity) {
                      showError(`Araçta yeterli stok yok. Mevcut: ${carStockForProduct?.quantity || 0}`);
                      return;
                    }

                    const totalPrice = unitPrice * quantity;
                    await carSalesAPI.create({
                      carId: selectedCar.id,
                      productId: saleProductId,
                      quantity,
                      unitPrice,
                      totalPrice,
                      notes: ''
                    });

                    // No need to change main warehouse stock; it was moved on transfer. Just refresh car stocks.
                    setShowSalesModal(false);
                    setSelectedCar(null);
                    setSaleProductId('');
                    setSaleQuantity('');

                    // Refresh UI
                    await fetchCarStocks();
                    showSuccess('Satış başarıyla kaydedildi');
                  } catch (error) {
                    console.error('Car sale error:', error);
                    showError('Satış yapılırken hata oluştu');
                  } finally {
                    setIsSavingSale(false);
                  }
                }}
                disabled={isSavingSale}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingSale ? 'İşleniyor...' : 'Satışı Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cars;
